import {type FC, useCallback, useState} from 'react';
import {JOURNEY_EVENT} from '../../../enums/tagging/index.js';
import {runAnalytics} from '../../analytics.js';

type TaggingFieldCheckBoxClickHandlerProps<P> = {
  taggingLabel: string;
  onBlur?: (...args: any[]) => void;
  onFocus?: (...args: any[]) => void;
  error?: any;
} & P;

/*
  Higher order component that wraps components such as TextFields and Dropdowns, and triggers the analytics function onBlur.
  Tracks dwell time and proxies any onBlur or onFocus functions that are passed to the component as a prop.
*/

const withTaggingFieldCheckBoxClickHandler = <P,>(Component: FC<P>) => {
  const FieldWithTagging: FC<TaggingFieldCheckBoxClickHandlerProps<P>> = ({
    taggingLabel,
    onBlur,
    onFocus,
    error,
    ...rest
  }: TaggingFieldCheckBoxClickHandlerProps<P>) => {
    const [startTime, setStartTime] = useState<number>(0);

    const handleOnBlur = useCallback(
      (...args: any) => {
        const checked: any = event?.target;

        const eventTaggingLabel = checked.checked
          ? `${taggingLabel}: Tick`
          : `${taggingLabel}: Untick`;

        const linkValue = checked.checked
          ? `${taggingLabel.replace(/ +/g, '').toLowerCase()}tick`
          : `${taggingLabel.replace(/ +/g, '').toLowerCase()}untick`;
        const qaAnswer = checked.checked ? 'Tick' : 'Untick';

        if (onBlur) {
          onBlur(...args);
        }

        if (taggingLabel) {
          runAnalytics((LBGAnalytics) => {
            const events = LBGAnalytics.events;

            events.send({
              JourneyEvent: JOURNEY_EVENT.INTERNAL_CLICK,
              EventNarrative: eventTaggingLabel,
              FieldName: taggingLabel,
              EventAction: JOURNEY_EVENT.FIELD_UPDATE,
              dwell_in_ms: Date.now() - startTime,
              LinkValue: `button/${linkValue}`,
              QAQuestion: taggingLabel,
              QAAnswer: qaAnswer,
            });
          });
        }
      },
      [onBlur, taggingLabel, startTime]
    );

    const handleOnFocus = useCallback(
      (...args: any) => {
        if (onFocus) {
          onFocus(...args);
        }

        setStartTime(Date.now());
      },
      [setStartTime, onFocus]
    );

    return (
      <Component
        {...(rest as P)}
        error={error}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
      />
    );
  };

  return FieldWithTagging;
};

export default withTaggingFieldCheckBoxClickHandler;
